/**
 * Anonymous User ID Management
 * Handles persistent anonymous user identity for duplicate detection
 */

const ANONYMOUS_USER_KEY = 'brick_figure_anonymous_user_id';

/**
 * Generate a new anonymous user ID (UUID v4)
 * @returns {string} UUID v4 string
 */
const generateAnonymousUserId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

/**
 * Get or create anonymous user ID
 * Persists in localStorage for session continuity
 * @returns {string} Anonymous user ID
 */
export const getAnonymousUserId = () => {
  try {
    // Try to get existing ID from localStorage
    let anonymousUserId = localStorage.getItem(ANONYMOUS_USER_KEY);
    
    if (!anonymousUserId) {
      // Generate new ID if none exists
      anonymousUserId = generateAnonymousUserId();
      localStorage.setItem(ANONYMOUS_USER_KEY, anonymousUserId);
      console.log(`[AnonymousUser] Generated new anonymous user ID: ${anonymousUserId}`);
    } else {
      console.log(`[AnonymousUser] Retrieved existing anonymous user ID: ${anonymousUserId}`);
    }
    
    return anonymousUserId;
  } catch (error) {
    // Fallback if localStorage is not available
    console.warn('[AnonymousUser] localStorage not available, using session-only ID');
    return generateAnonymousUserId();
  }
};

/**
 * Clear anonymous user ID (logout equivalent for anonymous users)
 */
export const clearAnonymousUserId = () => {
  try {
    localStorage.removeItem(ANONYMOUS_USER_KEY);
    console.log('[AnonymousUser] Anonymous user ID cleared');
  } catch (error) {
    console.warn('[AnonymousUser] Failed to clear anonymous user ID:', error);
  }
};

/**
 * Check if user has an existing anonymous session
 * @returns {boolean} True if anonymous ID exists in storage
 */
export const hasAnonymousSession = () => {
  try {
    return localStorage.getItem(ANONYMOUS_USER_KEY) !== null;
  } catch (error) {
    return false;
  }
};